import { PAGINATION_MAX_PAGE_SIZE } from '@pulsify/shared/dto'
import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { useMemo } from 'react'
import { organizerApi } from '~/entities/channel/api/organizer'
import { listingsApi } from '~/entities/listing/api'
import { confirmHostPublish, draftHostOf } from '~/entities/profile/model/host-publish'
import * as m from '~/paraglide/messages'
import { QueryKey, queryKey } from '~/shared/lib/query-keys'
import { MINUTES } from '~/shared/lib/time-constants'
import type { CreateEventInput, Event, ListingLocation, UpdateEventInput } from '~/shared/types'
import { eventsApi } from '../api'

const EVENTS_PAGE_SIZE = 20
const LIVE_ROOM_POLL_MS = 30_000

/**
 * Infinite-scroll events list (upcoming or past) for a business, or one of its host profiles,
 * narrowed to the events matching `search` when it is not empty.
 */
export function useEvents(
  businessId: string,
  when: 'upcoming' | 'past',
  hostProfileId?: string,
  search = '',
) {
  const q = search.trim()
  const query = useInfiniteQuery({
    queryKey: queryKey(QueryKey.EVENTS, businessId, when, hostProfileId ?? 'all', q),
    queryFn: ({ pageParam }) =>
      eventsApi.list(businessId, {
        startIndex: pageParam,
        pageSize: EVENTS_PAGE_SIZE,
        when,
        hostProfileId,
        q: q || undefined,
      }),
    placeholderData: keepPreviousData,
    initialPageParam: 0,
    getNextPageParam: (lastPage) => lastPage.window.nextStartIndex ?? undefined,
    staleTime: 2 * MINUTES,
    enabled: !!businessId,
  })

  const events = useMemo(() => query.data?.pages.flatMap((p) => p.data) ?? [], [query.data])

  return {
    events,
    total: query.data?.pages[0]?.window.total ?? 0,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isError: query.isError,
    error: query.error,
    hasNextPage: query.hasNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
    fetchNextPage: query.fetchNextPage,
    refetch: query.refetch,
  }
}

async function listAllInRange(
  businessId: string,
  when: 'upcoming' | 'past',
  params: { from: string; to: string; hostProfileId?: string; q?: string },
): Promise<Event[]> {
  const events: Event[] = []
  let startIndex: number | null = 0
  while (startIndex !== null) {
    const page = await eventsApi.list(businessId, {
      ...params,
      startIndex,
      pageSize: PAGINATION_MAX_PAGE_SIZE,
      when,
    })
    events.push(...page.data)
    startIndex = page.window.nextStartIndex
  }
  return events
}

/** Every event (upcoming and past) whose time overlaps `[from, to)`, for the calendar. */
export function useEventsInRange(
  businessId: string,
  range: { from: Date; to: Date },
  options: { hostProfileId?: string; search?: string; enabled?: boolean } = {},
) {
  const { hostProfileId, search = '', enabled = true } = options
  const q = search.trim() || undefined
  const from = range.from.toISOString()
  const to = range.to.toISOString()
  return useQuery({
    queryKey: queryKey(QueryKey.EVENTS, businessId, 'range', hostProfileId ?? 'all', from, to, q),
    queryFn: async () => {
      const params = { from, to, hostProfileId, q }
      const [upcoming, past] = await Promise.all([
        listAllInRange(businessId, 'upcoming', params),
        listAllInRange(businessId, 'past', params),
      ])
      return [...past, ...upcoming]
    },
    placeholderData: keepPreviousData,
    staleTime: 2 * MINUTES,
    enabled: enabled && !!businessId,
  })
}

export function useEvent(businessId: string, eventId: string) {
  return useQuery({
    queryKey: queryKey(QueryKey.EVENT, eventId),
    queryFn: () => eventsApi.getById(businessId, eventId),
    staleTime: 5 * MINUTES,
    enabled: !!businessId && !!eventId,
  })
}

export function useEventCheckinCode(businessId: string, eventId: string) {
  return useQuery({
    queryKey: queryKey(QueryKey.EVENT_CHECKIN_CODE, eventId),
    queryFn: () => eventsApi.checkinCode(businessId, eventId),
    staleTime: Number.POSITIVE_INFINITY,
    enabled: !!businessId && !!eventId,
  })
}

/** Who is in the event's Live Room right now; asked only while a delete dialog is open. */
export function useEventLiveRoom(businessId: string, eventId: string, enabled: boolean) {
  return useQuery({
    queryKey: queryKey(QueryKey.EVENT_LIVE_ROOM, eventId),
    queryFn: () => eventsApi.liveRoom(businessId, eventId),
    staleTime: 0,
    enabled: enabled && !!businessId && !!eventId,
  })
}

/** The event's Live Room head count, polled while the tab is visible. */
export function useEventLiveRoomPolling(businessId: string, eventId: string, enabled: boolean) {
  return useQuery({
    queryKey: queryKey(QueryKey.EVENT_LIVE_ROOM, eventId),
    queryFn: () => eventsApi.liveRoom(businessId, eventId),
    staleTime: 0,
    refetchInterval: LIVE_ROOM_POLL_MS,
    enabled: enabled && !!businessId && !!eventId,
  })
}

export function useEventAnnouncement(businessId: string, eventId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (text: string) => eventsApi.announce(businessId, eventId, text),
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: queryKey(QueryKey.EVENT_LIVE_ROOM, eventId) }),
  })
}

export function useEndEventLiveRoom(businessId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (eventId: string) => eventsApi.endLiveRoom(businessId, eventId),
    onSuccess: (_data, eventId) => {
      queryClient.invalidateQueries({ queryKey: queryKey(QueryKey.EVENT_LIVE_ROOM, eventId) })
      queryClient.invalidateQueries({ queryKey: queryKey(QueryKey.BUSINESS_OVERVIEW, businessId) })
    },
  })
}

interface CreateEventMutationInput {
  data: CreateEventInput
  listingLocation?: ListingLocation | null
}

export function useCreateEvent(businessId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ data, listingLocation }: CreateEventMutationInput) =>
      eventsApi.create(businessId, data, { listingLocation }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKey(QueryKey.EVENTS, businessId),
      })
      queryClient.invalidateQueries({
        queryKey: queryKey(QueryKey.SUBSCRIPTION_STATUS, businessId),
      })
    },
  })
}

export function useUpdateEvent(businessId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ eventId, data }: { eventId: string; data: Partial<UpdateEventInput> }) =>
      eventsApi.update(businessId, eventId, data),
    onSuccess: (_data, { eventId }) => {
      queryClient.invalidateQueries({
        queryKey: queryKey(QueryKey.EVENT, eventId),
      })
      queryClient.invalidateQueries({
        queryKey: queryKey(QueryKey.EVENTS, businessId),
      })
    },
  })
}

export function useDeleteEvent(businessId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (eventId: string) => eventsApi.delete(businessId, eventId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKey(QueryKey.EVENTS, businessId),
      })
      queryClient.invalidateQueries({
        queryKey: queryKey(QueryKey.SUBSCRIPTION_STATUS, businessId),
      })
    },
  })
}

export function usePublishEvent(businessId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (eventId: string) => {
      try {
        return await eventsApi.publish(businessId, eventId)
      } catch (error) {
        const host = draftHostOf(error)
        if (!host) throw error
        if (!(await confirmHostPublish(host))) throw new Error(m.eventNeedsPublicHost())
        if (host.listingId) await listingsApi.publish(businessId, host.listingId)
        else await organizerApi.setPublished(host.id, true)
        queryClient.invalidateQueries({
          queryKey: queryKey(QueryKey.ORGANIZATION_PROFILES, businessId),
        })
        return eventsApi.publish(businessId, eventId)
      }
    },
    onSuccess: (_data, eventId) => {
      queryClient.invalidateQueries({
        queryKey: queryKey(QueryKey.EVENT, eventId),
      })
      queryClient.invalidateQueries({
        queryKey: queryKey(QueryKey.EVENTS, businessId),
      })
      queryClient.invalidateQueries({
        queryKey: queryKey(QueryKey.SUBSCRIPTION_STATUS, businessId),
      })
    },
  })
}

export function useCancelEvent(businessId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (eventId: string) => eventsApi.cancel(businessId, eventId),
    onSuccess: (_data, eventId) => {
      queryClient.invalidateQueries({
        queryKey: queryKey(QueryKey.EVENT, eventId),
      })
      queryClient.invalidateQueries({
        queryKey: queryKey(QueryKey.EVENTS, businessId),
      })
      queryClient.invalidateQueries({
        queryKey: queryKey(QueryKey.SUBSCRIPTION_STATUS, businessId),
      })
    },
  })
}

export function useUnlistEvent(businessId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (eventId: string) => eventsApi.unlist(businessId, eventId),
    onSuccess: (_data, eventId) => {
      queryClient.invalidateQueries({
        queryKey: queryKey(QueryKey.EVENT, eventId),
      })
      queryClient.invalidateQueries({
        queryKey: queryKey(QueryKey.EVENTS, businessId),
      })
      queryClient.invalidateQueries({
        queryKey: queryKey(QueryKey.SUBSCRIPTION_STATUS, businessId),
      })
    },
  })
}
